import { startOfDay } from "date-fns";

import {
  ApprovalStatus,
  TimeTrackingSessionKeys,
} from "../timeTrackingPayroll.constants";
import { TimeSheet } from "../timeTrackingPayroll.interfaces";
import type { PayrollFormRow } from "../timeTrackingPayroll.types";

import { BadgeVariants } from "@/components/ui/Badge";
import { DateStringToZeroDate } from "@/utils/core/generalUtils";

// Time tracking constants
export const SECONDS_PER_HOUR = 3600;
export const SECONDS_PER_DAY = 86400;
export const EVENING_START_SECONDS = 20 * SECONDS_PER_HOUR; // 8 PM in seconds
export const MORNING_END_SECONDS = 6 * SECONDS_PER_HOUR; // 6 AM in seconds
export const FIRST_BREAK_DELAY_HOURS = 4; // First break occurs 4 hours after start
export const SECOND_BREAK_DELAY_HOURS = 10.5; // Second break occurs 10.5 hours after start
export const FIRST_BREAK_DURATION_HOURS = 0.5; // First break is always 30 minutes
export const WEEKEND_DAYS = [0, 6]; // Sunday = 0, Saturday = 6

/**
 * Checks if all timesheet entries across all dates are verified
 * @param {TimeSheet[]} timesheets - Array of timesheet data
 * @returns {boolean} boolean indicating if all entries are verified
 */
export const areAllTimesheetsVerified = (timesheets: TimeSheet[]): boolean => {
  return timesheets.every((timesheet) => {
    if (!timesheet.days || typeof timesheet.days !== "object") {
      return true;
    }

    // Check all entries across all dates
    return Object.values(timesheet.days).every((dayEntries) =>
      Array.isArray(dayEntries)
        ? dayEntries.every((entry) => entry.isVerified === true)
        : true,
    );
  });
};

/**
 * Processes raw API response data for timesheet operations
 * @param {T} data - Raw API response data
 * @returns {{ data: TimeSheet[] }} Processed timesheet data
 */
export const processTimesheetApiResponse = <T>(
  data: T,
): { data: TimeSheet[] } => {
  const resData = data as GeneralSingleResponse<TimeSheet[]>;

  return {
    data: resData?.data || [],
  };
};

/**
 * Safely extracts employee name from timesheet data
 * @param {TimeSheet[]} timesheets - Array of timesheet data
 * @returns {string} Employee name or empty string
 */
export const getEmployeeNameFromTimesheets = (
  timesheets: TimeSheet[],
): string => {
  return timesheets[0]?.employeeName || "";
};

/**
 * Gets fortnight value from session storage
 * @returns {string} Fortnight string or empty string
 */
export const getFortnightFromSession = (): string => {
  return sessionStorage.getItem(TimeTrackingSessionKeys.FORTNIGHT) || "";
};

/**
 * Returns the total hour value based on employee type.
 * If employee type is FULLTIME, returns 80, else returns 48.
 * @param {EmploymentType} employmentType - The employment type of the employee
 * @returns {number} - The total hour value based on employee type
 */
export const getTotalHourByEmployeeType = (employmentType?: string): number => {
  return employmentType === "FULLTIME" ? 80 : 48;
};

type ShiftData = {
  date: string;
  starttime: number;
  endtime: number;
  break_hour: number;
  travel_hour: number;
};

type ActualCurrentHours = {
  weekend: number;
  overnight: number;
  normal: number;
  total: number;
};

/**
 * Process timesheet form data to extract shifts for calculation
 * @param {PayrollFormRow[]} formRows - Form data rows
 * @returns {ShiftData[]} - Array of shifts
 */
export function processTimesheetDataToShifts(
  formRows: PayrollFormRow[],
): ShiftData[] {
  const shifts: ShiftData[] = [];

  formRows.forEach((row) => {
    // Only process rows that have startTime, endTime (including 0), projectId, and isTwelve is not false
    if (
      row.startTime !== undefined &&
      row.endTime !== undefined &&
      row.projectId &&
      row.isTwelve !== false &&
      row.isStartTwelve !== false &&
      row.isEndTwelve !== false
    ) {
      shifts.push({
        date: DateStringToZeroDate((row.date as Date).toString()) || "",
        starttime: row.startTime,
        endtime: row.endTime,
        break_hour: row.breakTime || 0,
        travel_hour: row.travelTime || 0,
      });
    }
  });

  return shifts;
}

/**
 * Calculate work hours for a single shift (basic calculation)
 * @param {number} startTime - Start time in seconds
 * @param {number} endTime - End time in seconds
 * @param {number} breakSeconds - Break time in seconds
 * @returns {number} - Work hours
 */
function calculateWorkHours(
  startTime: number,
  endTime: number,
  breakSeconds: number,
): number {
  let totalSeconds;

  if (startTime < endTime) {
    totalSeconds = endTime - startTime;
  } else if (startTime === endTime) {
    totalSeconds = 86400;
  } else {
    // Overnight shift
    totalSeconds = 86400 - startTime + endTime;
  }

  const workingSeconds = Math.max(0, totalSeconds - breakSeconds);

  return workingSeconds / 3600; // Convert to hours
}

/**
 * Determine if a given time falls within overnight hours (8 PM to 6 AM)
 * @param {number} timeInSeconds - Time in seconds from midnight
 * @returns {boolean} - True if time is in overnight hours
 */
export function isOvernightTime(timeInSeconds: number): boolean {
  // Normalize time to 24-hour format
  const normalizedTime = timeInSeconds % SECONDS_PER_DAY;

  return (
    normalizedTime >= EVENING_START_SECONDS ||
    normalizedTime < MORNING_END_SECONDS
  );
}

/**
 * Calculate shift hours breakdown into normal and overnight with proper break allocation
 * @param {number} startTime - Start time in seconds
 * @param {number} endTime - End time in seconds
 * @param {number} breakSeconds - Break time in seconds
 * @returns {object} - Normal and overnight hours
 */
function calculateShiftHours(
  startTime: number,
  endTime: number,
  breakSeconds: number,
): { normalHours: number; overnightHours: number } {
  let normalHours = 0;
  let overnightHours = 0;

  // First calculate total hours without break deduction
  if (startTime < endTime) {
    // Same day shift
    if (endTime < MORNING_END_SECONDS || startTime > EVENING_START_SECONDS) {
      // Entirely overnight
      const totalHours = calculateWorkHours(startTime, endTime, 0);
      overnightHours = totalHours;
    } else if (
      startTime >= MORNING_END_SECONDS &&
      endTime <= EVENING_START_SECONDS
    ) {
      // Entirely normal hours
      const totalHours = calculateWorkHours(startTime, endTime, 0);
      normalHours = totalHours;
    } else {
      // Mixed shift - split into normal and overnight
      if (startTime < MORNING_END_SECONDS && endTime > EVENING_START_SECONDS) {
        const overnightFromStart = MORNING_END_SECONDS - startTime;
        const normalInMiddle = EVENING_START_SECONDS - MORNING_END_SECONDS;
        const overnightAtEnd = endTime - EVENING_START_SECONDS;

        overnightHours =
          (overnightFromStart + overnightAtEnd) / SECONDS_PER_HOUR;
        normalHours = normalInMiddle / SECONDS_PER_HOUR;
      } else if (
        startTime < MORNING_END_SECONDS &&
        endTime >= MORNING_END_SECONDS
      ) {
        // Starts in overnight, ends in normal
        const overnightSeconds = MORNING_END_SECONDS - startTime;
        const normalSeconds = endTime - MORNING_END_SECONDS;
        overnightHours = overnightSeconds / SECONDS_PER_HOUR;
        normalHours = normalSeconds / SECONDS_PER_HOUR;
      } else if (
        startTime < EVENING_START_SECONDS &&
        endTime > EVENING_START_SECONDS
      ) {
        // Starts in normal, ends in overnight
        const normalSeconds = EVENING_START_SECONDS - startTime;
        const overnightSeconds = endTime - EVENING_START_SECONDS;
        normalHours = normalSeconds / SECONDS_PER_HOUR;
        overnightHours = overnightSeconds / SECONDS_PER_HOUR;
      }
    }
  } else {
    // Overnight shift (crosses midnight)
    const beforeMidnight = SECONDS_PER_DAY - startTime;
    const afterMidnight = endTime;

    // Check if the entire shift is overnight
    const isFullOvernight =
      startTime >= EVENING_START_SECONDS && endTime < MORNING_END_SECONDS;

    if (isFullOvernight) {
      // All overnight
      const totalSeconds = beforeMidnight + afterMidnight;
      overnightHours = totalSeconds / SECONDS_PER_HOUR;
    } else {
      // Mixed overnight shift
      // Before midnight
      if (startTime >= EVENING_START_SECONDS) {
        overnightHours += beforeMidnight / SECONDS_PER_HOUR;
      } else {
        if (startTime < MORNING_END_SECONDS) {
          // Shift starts in early morning (overnight period)
          const overnightFromStart = MORNING_END_SECONDS - startTime;
          const normalFromMorningToEvening =
            EVENING_START_SECONDS - MORNING_END_SECONDS;
          const overnightFromEveningToMidnight =
            beforeMidnight - (overnightFromStart + normalFromMorningToEvening);

          overnightHours += overnightFromStart / SECONDS_PER_HOUR;
          normalHours += normalFromMorningToEvening / SECONDS_PER_HOUR;
          overnightHours += overnightFromEveningToMidnight / SECONDS_PER_HOUR;
        } else {
          // Shift starts after 6 AM
          const normalBeforeMidnight = EVENING_START_SECONDS - startTime;
          const overnightBeforeMidnight = beforeMidnight - normalBeforeMidnight;
          normalHours += normalBeforeMidnight / SECONDS_PER_HOUR;
          overnightHours += overnightBeforeMidnight / SECONDS_PER_HOUR;
        }
      }

      // After midnight
      if (endTime < MORNING_END_SECONDS) {
        overnightHours += afterMidnight / SECONDS_PER_HOUR;
      } else {
        const overnightAfterMidnight = MORNING_END_SECONDS;
        const normalAfterMidnight = endTime - MORNING_END_SECONDS;
        overnightHours += overnightAfterMidnight / SECONDS_PER_HOUR;
        normalHours += normalAfterMidnight / SECONDS_PER_HOUR;
      }
    }
  }

  // Now apply break allocation based on timing
  if (breakSeconds > 0) {
    const breakHours = breakSeconds / SECONDS_PER_HOUR;
    const shiftDurationSeconds =
      startTime < endTime
        ? endTime - startTime
        : SECONDS_PER_DAY - startTime + endTime;

    // Define break times
    const firstBreakTime =
      startTime + FIRST_BREAK_DELAY_HOURS * SECONDS_PER_HOUR;
    const secondBreakTime =
      startTime + SECOND_BREAK_DELAY_HOURS * SECONDS_PER_HOUR;

    // First break (always 30 minutes or 0.5 hours)
    const firstBreakAmount = Math.min(FIRST_BREAK_DURATION_HOURS, breakHours);

    // Determine where first break falls and handle spillover
    if (isOvernightTime(firstBreakTime)) {
      if (overnightHours >= firstBreakAmount) {
        overnightHours -= firstBreakAmount;
      } else {
        // Spillover: deduct what we can from overnight, rest from normal
        const spillover = firstBreakAmount - overnightHours;
        overnightHours = 0;
        normalHours = Math.max(0, normalHours - spillover);
      }
    } else {
      if (normalHours >= firstBreakAmount) {
        normalHours -= firstBreakAmount;
      } else {
        // Spillover: deduct what we can from normal, rest from overnight
        const spillover = firstBreakAmount - normalHours;
        normalHours = 0;
        overnightHours = Math.max(0, overnightHours - spillover);
      }
    }

    // Second break (remaining break time)
    const remainingBreak = breakHours - firstBreakAmount;

    if (remainingBreak > 0) {
      let secondBreakDeductionTime;

      // If shift is less than 10.5 hours, use end time for second break
      if (shiftDurationSeconds < SECOND_BREAK_DELAY_HOURS * SECONDS_PER_HOUR) {
        secondBreakDeductionTime = endTime;
      } else {
        secondBreakDeductionTime = secondBreakTime;
      }

      // Determine where second break falls and handle spillover
      if (isOvernightTime(secondBreakDeductionTime)) {
        if (overnightHours >= remainingBreak) {
          overnightHours -= remainingBreak;
        } else {
          // Spillover: deduct what we can from overnight, rest from normal
          const spillover = remainingBreak - overnightHours;
          overnightHours = 0;
          normalHours = Math.max(0, normalHours - spillover);
        }
      } else {
        if (normalHours >= remainingBreak) {
          normalHours -= remainingBreak;
        } else {
          // Spillover: deduct what we can from normal, rest from overnight
          const spillover = remainingBreak - normalHours;
          normalHours = 0;
          overnightHours = Math.max(0, overnightHours - spillover);
        }
      }
    }
  }

  return {
    normalHours: Math.max(0, normalHours),
    overnightHours: Math.max(0, overnightHours),
  };
}

/**
 * Calculate actual current hours from timesheet entries (without tax/cash constraints)
 * @param {ShiftData[]} shifts - Array of shift objects
 * @returns {ActualCurrentHours} - Actual hours by category
 */
export function calculateActualCurrentHours(
  shifts: ShiftData[],
): ActualCurrentHours {
  let totalWeekendHours = 0;
  let totalOvernightHours = 0;
  let totalNormalHours = 0;

  // Process each shift
  shifts.forEach((shift) => {
    const { date, starttime, endtime, break_hour = 0, travel_hour = 0 } = shift;
    const breakSeconds = break_hour;
    const travelHours = travel_hour / SECONDS_PER_HOUR;

    const dayOfWeek = new Date(date).getDay();
    const isWeekend = WEEKEND_DAYS.includes(dayOfWeek);

    if (isWeekend) {
      // Weekend hours are determined by the actual date
      const workHours = calculateWorkHours(starttime, endtime, breakSeconds);
      totalWeekendHours += workHours;
    } else {
      // For weekdays, split hours between normal and overnight based on time ranges
      const { normalHours, overnightHours } = calculateShiftHours(
        starttime,
        endtime,
        breakSeconds,
      );
      totalNormalHours += normalHours;
      totalOvernightHours += overnightHours;
    }
    // Add travel hours to normal hours regardless of weekend/weekday
    // Travel hours are always considered normal hours
    totalNormalHours += travelHours;
  });

  return {
    weekend: Math.round(totalWeekendHours * 100) / 100,
    overnight: Math.round(totalOvernightHours * 100) / 100,
    normal: Math.round(totalNormalHours * 100) / 100,
    total:
      Math.round(
        (totalWeekendHours + totalOvernightHours + totalNormalHours) * 100,
      ) / 100,
  };
}

/**
 * Calculate actual current timesheet hours without constraints
 * @param {PayrollFormRow[]} formRows - Form data rows
 * @returns {ActualCurrentHours} - Actual hours by category
 */
export function calculateActualCurrentTimesheetHours(
  formRows: PayrollFormRow[],
): ActualCurrentHours {
  const shifts = processTimesheetDataToShifts(formRows);

  return calculateActualCurrentHours(shifts);
}

/**
 * Formats approval status for display
 * @param {ApprovalStatus} status - Approval status
 * @param {{ approved: string; voided: string; approvalSent: string; notSent: string }} [labels] - Optional localized labels for approval status text
 * @param {string} labels.approved - Display label for approved status
 * @param {string} labels.voided - Display label for voided status
 * @param {string} labels.approvalSent - Display label for approval-sent status
 * @param {string} labels.notSent - Display label for default not-sent status
 * @returns {object} - Formatted status with text and variant
 */
export const formatApprovalStatus = (
  status: ApprovalStatus,
  labels?: {
    approved: string;
    voided: string;
    approvalSent: string;
    notSent: string;
  },
): { text: string; variant: string } => {
  const statusLabels = labels ?? {
    approved: "Approved",
    voided: "Voided",
    approvalSent: "Approval Sent",
    notSent: "Not Sent",
  };

  switch (status) {
    case ApprovalStatus.APPROVED:
      return { text: statusLabels.approved, variant: "success" };
    case ApprovalStatus.VOIDED:
      return { text: statusLabels.voided, variant: "destructive" };
    case ApprovalStatus.APPROVAL_SENT:
      return { text: statusLabels.approvalSent, variant: "warning" };
    default:
      return { text: statusLabels.notSent, variant: "default" };
  }
};

/**
 * Convert API employment type to display value
 * @param {EmploymentType} type - Employment type
 * @param {{ fullTime: string; partTime: string; casual: string }} [labels] - Optional localized labels for employment type display
 * @param {string} labels.fullTime - Display label for full-time employment
 * @param {string} labels.partTime - Display label for part-time employment
 * @param {string} labels.casual - Display label for casual employment
 * @returns {string} Display value
 */
export const getEmploymentTypeDisplay = (
  type?: string,
  labels?: {
    fullTime: string;
    partTime: string;
    casual: string;
  },
): string => {
  const value = String(type || "").toUpperCase();
  const employmentTypeLabels = labels ?? {
    fullTime: "Full Time",
    partTime: "Part Time",
    casual: "Casual",
  };

  if (value === "FULLTIME" || value === "FULL TIME") {
    return employmentTypeLabels.fullTime;
  }

  if (value === "PARTTIME" || value === "PART TIME") {
    return employmentTypeLabels.partTime;
  }

  if (value === "CASUAL") {
    return employmentTypeLabels.casual;
  }

  return String(type || "");
};

/**
 * Get the appropriate badge variant based on approval status
 * @param {string} variant - Status variant from formatApprovalStatus
 * @returns {BadgeVariants} Badge variant
 */
export const getStatusBadgeVariant = (variant: string): BadgeVariants => {
  switch (variant) {
    case "success":
      return "lightSuccessWithBorder";
    case "warning":
      return "yellowWithBorder";
    case "destructive":
      return "lightDestructiveWithBorder";
    default:
      return "greyWithBorder";
  }
};

export const TIMESHEET_ENTRY_COLORS = {
  VERIFIED: "bg-[#22C55E]",
  OVER_TWELVE: "bg-[#F50C0C]",
  OVER_TEN: "bg-[#F59E0C]",
  NORMAL: "bg-[#334155]",
} as const;

/**
 * Returns the background class for a timesheet day entry.
 * Verified entries always use green; otherwise hours determine severity color.
 * @param {boolean} isVerified - Whether the entry is verified
 * @param {number} hours - Total entry hours
 * @returns {string} Tailwind background class
 */
export const getTimesheetEntryColorClass = (
  isVerified = false,
  hours = 0,
): string => {
  if (isVerified) {
    return TIMESHEET_ENTRY_COLORS.VERIFIED;
  }

  if (hours > 12) {
    return TIMESHEET_ENTRY_COLORS.OVER_TWELVE;
  }

  if (hours > 10) {
    return TIMESHEET_ENTRY_COLORS.OVER_TEN;
  }

  return TIMESHEET_ENTRY_COLORS.NORMAL;
};

/**
 * Calculate total time for a specific breakdown title
 * @param {Array} breakDowns - Array of breakdown objects with title and time properties
 * @param {string} title - The title to filter by
 * @returns {number} - Total time for the specified title
 */
export const calculateBreakdownTotal = (
  breakDowns: { title: string; time: number }[] | undefined,
  title: string,
): number => {
  return (
    breakDowns
      ?.filter((b) => b.title === title)
      .reduce((acc, b) => acc + b.time, 0) || 0
  );
};

/**
 * Filters fortnight days to hide all future dates after today
 * Returns only dates up to and including today, regardless of fortnight period
 * @param {Date[]} allDays - All days in the fortnight
 * @returns {Date[]} Filtered array of dates up to and including today
 */
export const filterFortnightDays = (allDays: Date[]): Date[] => {
  const today = startOfDay(new Date());

  // Filter out all future dates (dates after today)
  const filteredDays = allDays.filter((day) => {
    const dayStart = startOfDay(day);

    return dayStart <= today;
  });

  return filteredDays;
};
